import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { Collapsible, Label } from "@fronterahealth/frontera-ui-components";

import { ABLLSSkillArea, AssessmentSession } from "@api/assessments/assessment-schema-types";
import { FillBar } from "@components/AssessmentFormRenderer/components/FormSkillChecklist/FillBar";
import { SessionRail } from "@components/AssessmentFormRenderer/components/FormSkillChecklist/SessionRail";
import { SkillChecklistSection } from "@components/AssessmentFormRenderer/types/layoutTypes";

interface FormScoreChecklistProps {
  section: SkillChecklistSection;
}

type CSSVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

/**
 * Renders an array of table objects, each with its own column headers and rows.
 * Each element is shaped like { "Table Name": string, "Column Headers": string[], "Rows": Record[] }.
 *
 * Uses watch/setValue instead of register to avoid the zod resolver's
 * internal regex matching on field paths — column headers can contain
 * regex-special characters like parentheses.
 */
export const FormSkillChecklist: React.FC<FormScoreChecklistProps> = ({ section }) => {
  const { watch, setValue } = useFormContext();
  const tables: ABLLSSkillArea[] = watch(section.dataKey) ?? [];
  const sessions: AssessmentSession[] = watch(section.sessionDataKey) ?? [];

  // Fall back to the first session so async-loaded sessions aren't frozen out.
  const [selectedSessionId, setSelectedSessionId] = useState<number | undefined>(undefined);
  const currentSessionId =
    selectedSessionId !== undefined && sessions.some((s) => s.Id === selectedSessionId)
      ? selectedSessionId
      : sessions?.[0]?.Id;

  const handleScore = (areaName: string, taskCode: string, newScore: number | null) => {
    const newValue = tables.map((area) =>
      area.Name === areaName
        ? {
            ...area,
            Tasks: area.Tasks.map((task) => {
              if (task.Code !== taskCode) return task;
              if (newScore === null) {
                return { ...task, Scores: task.Scores.filter((s) => s.SessionId !== currentSessionId) };
              }
              const currentIdx = sessions.findIndex((s) => s.Id === currentSessionId);
              const sessionIdx = (id: number) => sessions.findIndex((s) => s.Id === id);
              const retained = task.Scores.filter((s) => {
                if (s.SessionId === currentSessionId) return false;
                const sIdx = sessionIdx(s.SessionId);
                return sIdx < currentIdx || (s.Score ?? 0) > newScore;
              });
              const updated = [...retained, { SessionId: currentSessionId, Score: newScore, Assessed: true }].sort(
                (a, b) => sessionIdx(a.SessionId) - sessionIdx(b.SessionId),
              );
              return { ...task, Scores: updated };
            }),
          }
        : area,
    );

    setValue(section.dataKey, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  const vars: CSSVars = sessions.reduce((res, current) => {
    // Color is optional (partial parses may omit it); fall back so the fill/rail
    // color cues never render as an unset CSS variable.
    res[`--chart-${current.Id}`] = current.Color || "#000000";
    return res;
  }, {} as CSSVars);

  if (currentSessionId === undefined) {
    return (
      <div className="flex flex-col gap-2">
        <Label displayType="loud">{section.label}</Label>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" style={vars}>
      <Label displayType="loud">{section.label}</Label>
      <SessionRail sessions={sessions} currentSessionId={currentSessionId} onSelectSession={setSelectedSessionId} />
      {tables.map((row) => (
        <Collapsible
          key={row.Name}
          headerContent={
            <div className="flex items-center justify-between bg-limestone-200 w-full px-4 py-2 border rounded-t-lg">
              <div className="flex items-center gap-2 flex-1 text-left">
                <span className="text-sm text-gray-700 flex-1">{row.Name}</span>
              </div>
            </div>
          }
          bodyContent={
            <div className="space-y-3 bg-limestone-50 border rounded-b-lg px-4 py-2">
              {[...row.Tasks].reverse().map((task) => (
                <div key={task.Code} className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-semibold text-gray-900 shrink-0">{task.Code}</span>
                      <span className="text-sm text-gray-700 truncate">{task.Name}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <FillBar
                      task={task}
                      sessions={sessions}
                      onScore={(newScore) => handleScore(row.Name, task.Code, newScore)}
                      currentSessionId={currentSessionId}
                    />
                  </div>
                </div>
              ))}
            </div>
          }
          headerIsClickable={true}
        />
      ))}
    </div>
  );
};
